import { AxiosError, type TCommercial, deleteUser } from "@/api";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useCallback, useState } from "react";
import {
  Button,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";

// Inline-row Delete action for supplier users. Confirm dialog + admin
// endpoint via deleteUser(id, supplierId). Mirrors react-admin's
// DeleteWithConfirmButton UX without forcing routing/dependentId plumbing.
export const SupplierDeleteUserButton = () => {
  const record = useRecordContext<TCommercial>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!record?.id || !record.supplierId) return;
    setLoading(true);
    try {
      await deleteUser(String(record.id), record.supplierId);
      notify("Usuario eliminado", { type: "success" });
      refresh();
      setOpen(false);
    } catch (error: any) {
      let errorMessage: string | undefined;
      if (error instanceof AxiosError) {
        errorMessage = error.response?.data?.message;
      }
      notify(errorMessage || "Error al eliminar el usuario.", {
        type: "warning",
      });
    } finally {
      setLoading(false);
    }
  }, [record, notify, refresh]);

  if (!record) return null;

  return (
    <>
      <Tooltip title="Eliminar usuario">
        <IconButton
          color="error"
          size="small"
          onClick={() => setOpen(true)}
          disabled={loading}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => !loading && setOpen(false)}>
        <DialogTitle>Eliminar usuario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Eliminar a <b>{record.email}</b>? Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancelar"
            onClick={() => setOpen(false)}
            disabled={loading}
          />
          <Button
            label="Eliminar"
            color="error"
            variant="contained"
            onClick={handleConfirm}
            disabled={loading}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
